import { createContext, useCallback, useContext, useMemo, useState } from 'react';

const OrderContext = createContext(null);

const STORAGE_KEY = 'tub.lastOrder';

/**
 * Holds the most recent order so /order/confirmed can render after checkout.
 * sessionStorage (not local) so a refresh keeps the confirmation, but the order
 * doesn't linger in a new session.
 */
export function OrderProvider({ children }) {
  const [order, setOrder] = useState(() => {
    try {
      const raw = window.sessionStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });

  const saveOrder = useCallback((next) => {
    setOrder(next);
    try {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      /* storage unavailable — in-memory is enough for the confirmation screen */
    }
  }, []);

  const value = useMemo(() => ({ order, saveOrder }), [order, saveOrder]);

  return <OrderContext.Provider value={value}>{children}</OrderContext.Provider>;
}

export function useOrder() {
  const ctx = useContext(OrderContext);
  if (!ctx) throw new Error('useOrder must be used inside <OrderProvider>');
  return ctx;
}
